import type { Letters } from './scoring';
import { findOptimalPositions } from './scoring';

interface RoundWord {
  word: string;
  isUnique: boolean;
  finalScore: number;
}

interface RoundPlayer {
  nickname: string;
  words: RoundWord[];
}

export interface Highlight {
  word: string;
  nickname: string;
  finalScore: number;
  sharedBy?: number;
}

export interface RoundHighlightsResult {
  longest?: Highlight;
  rarest?: Highlight;
  overlap?: Highlight;
}

/** Жадный матч по порядку букв: первое вхождение l1, затем l2 после него, l3 после. */
export function pathIndices(word: string, letters: Letters): [number, number, number] | null {
  const normalized = word.toLowerCase().replace(/-/g, '').replace(/ё/g, 'е');
  const normLetters: Letters = [
    letters[0] === 'ё' ? 'е' : letters[0],
    letters[1] === 'ё' ? 'е' : letters[1],
    letters[2] === 'ё' ? 'е' : letters[2],
  ];
  const pos = findOptimalPositions(normalized, normLetters);
  if (!pos) return null;
  return [pos.l1, pos.l2, pos.l3];
}

/** Агрегирует три нарративных хайлайта раунда из полных списков слов всех игроков. */
export function roundHighlights(players: RoundPlayer[], _letters: string): RoundHighlightsResult {
  const all = players.flatMap(p => p.words.map(w => ({ ...w, nickname: p.nickname })));
  if (all.length === 0) return {};

  // longest: максимальная длина, тай → больший finalScore, затем первое встреченное
  let longest = all[0];
  for (const w of all.slice(1)) {
    if (
      w.word.length > longest.word.length ||
      (w.word.length === longest.word.length && w.finalScore > longest.finalScore)
    ) {
      longest = w;
    }
  }

  // rarest: среди isUnique — максимальный finalScore, тай → длиннее
  let rarest: typeof all[0] | undefined;
  for (const w of all) {
    if (!w.isUnique) continue;
    if (
      !rarest ||
      w.finalScore > rarest.finalScore ||
      (w.finalScore === rarest.finalScore && w.word.length > rarest.word.length)
    ) {
      rarest = w;
    }
  }

  // overlap: слово у ≥2 игроков, тай → больше нашедших, затем длиннее
  const counts = new Map<string, { count: number; word: string; nickname: string; finalScore: number }>();
  for (const p of players) {
    const seen = new Set<string>();
    for (const w of p.words) {
      const key = w.word.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      const existing = counts.get(key);
      if (existing) {
        existing.count++;
      } else {
        counts.set(key, { count: 1, word: w.word, nickname: p.nickname, finalScore: w.finalScore });
      }
    }
  }

  let overlapEntry: { count: number; word: string; nickname: string; finalScore: number } | undefined;
  for (const entry of counts.values()) {
    if (entry.count < 2) continue;
    if (
      !overlapEntry ||
      entry.count > overlapEntry.count ||
      (entry.count === overlapEntry.count && entry.word.length > overlapEntry.word.length)
    ) {
      overlapEntry = entry;
    }
  }

  return {
    longest: { word: longest.word, nickname: longest.nickname, finalScore: longest.finalScore },
    rarest: rarest
      ? { word: rarest.word, nickname: rarest.nickname, finalScore: rarest.finalScore }
      : undefined,
    overlap: overlapEntry
      ? {
          word: overlapEntry.word,
          nickname: overlapEntry.nickname,
          finalScore: overlapEntry.finalScore,
          sharedBy: overlapEntry.count,
        }
      : undefined,
  };
}
